import React, { useEffect, useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'

import { Button, Spinner } from '../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../constants/colors'
import { useTranslation } from '../hooks/useIntractionTranslate'
import { SettingsSection } from '../settings'
import type { SettingsFieldValue, SettingsValues } from '../settings'
import { buildCommunicationSettingsSchema } from './communicationSettingsSchema'
import { communicationPreferencesToValues, valuesToCommunicationPreferences } from './mapCommunicationPreferences'
import { useCommunicationPreferences } from './useCommunicationPreferences'

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

// Drop-in settings card for per-user communication opt-outs. Categories are filtered
// to the union of what the user's org roles actually receive (see the hook).
export const CommunicationPreferencesSection: React.FC = () => {
  const { t } = useTranslation()
  const { preferences, audiences, save } = useCommunicationPreferences()

  const schema = useMemo(() => buildCommunicationSettingsSchema(audiences), [audiences])
  const initialValues = useMemo(() => communicationPreferencesToValues(preferences), [preferences])

  const [values, setValues] = useState<SettingsValues>(initialValues)
  const [status, setStatus] = useState<SaveStatus>('idle')

  const initialKey = JSON.stringify(initialValues)
  useEffect(() => {
    setValues(initialValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey])

  const isDirty = JSON.stringify(values) !== initialKey
  const isSaving = status === 'saving'

  // Nothing live for this audience (e.g. facilitators until their categories ship) — hide the card.
  if (schema.fields.length === 0) return null

  const handleChange = (key: string, value: SettingsFieldValue) => {
    setStatus('idle')
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  const handleSave = async () => {
    setStatus('saving')
    try {
      await save(valuesToCommunicationPreferences(values, preferences))
      setStatus('saved')
    } catch {
      setStatus('error')
    }
  }

  return (
    <SettingsSection
      schema={schema}
      values={values}
      onChange={handleChange}
      disabled={isSaving}
      footer={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {status === 'saved' && !isDirty && (
            <Typography sx={{ fontSize: '13px', color: INTERAX_COLORS.green }}>
              {t('settings.communication.saved')}
            </Typography>
          )}
          {status === 'error' && (
            <Typography sx={{ fontSize: '13px', color: INTERAX_COLORS.redError }}>
              {t('settings.communication.saveError')}
            </Typography>
          )}
          <Button variant="primary" onClick={handleSave} disabled={!isDirty || isSaving}>
            {isSaving ? <Spinner size="small" /> : t('settings.communication.save')}
          </Button>
        </Box>
      }
    />
  )
}
